import React from 'react';
import { toHTML } from 'slack-markdown';

import { cn } from '@epoch-ui/lib/utils';
import { Separator } from '@epoch-ui/components/Separator';
import { CoverPhoto } from '@epoch-ui/components/CoverPhoto';
import { getEventDateString } from 'utils/eventUtils';
import { EditableEvent, PublishedEvent } from 'types/event.types';

// Root Component
interface SlackPreviewProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
  appLogoUrl?: string;
  appName?: string;
  timestamp?: string;
}

const SlackPreview: React.FC<SlackPreviewProps> = ({
  children,
  appLogoUrl = 'https://epoch-company-logos.s3.amazonaws.com/logo.svg',
  appName = 'Epoch',
  timestamp = '11:00 AM',
  className,
  ...props
}) => {
  return (
    <div
      className={cn(
        'tw-flex tw-items-start tw-gap-2 tw-rounded-lg tw-border tw-border-solid tw-border-gray-300 tw-p-4',
        className
      )}
      {...props}
    >
      <img src={appLogoUrl} alt={`${appName} Logo`} className="tw-mt-1 tw-w-9" />
      <div className="tw-flex tw-grow tw-flex-col">
        {/* App info */}
        <div className="tw-flex tw-gap-1.5">
          <p className="tw-m-0 tw-text-sm tw-font-semibold">{appName}</p>
          <p className="tw-leading-0 tw-m-0 tw-rounded-lg tw-bg-light-grey tw-px-1 tw-py-0 tw-text-[9px] tw-font-medium tw-text-primary-grey">
            APP
          </p>

          <p className="tw-leading-0 tw-m-0 tw-text-xs tw-font-normal tw-text-medium-grey">
            {timestamp}
          </p>
        </div>

        {children}
      </div>
    </div>
  );
};

// Body Component
interface SlackMessageProps extends React.HTMLAttributes<HTMLDivElement> {
  children?: React.ReactNode;
  markdownContent?: string;
}

const SlackMessage: React.FC<SlackMessageProps> = ({
  children,
  className,
  markdownContent = '',
  ...props
}) => {
  const content = markdownContent ? toHTML?.(markdownContent) : markdownContent;

  return (
    <div
      className={cn(
        'tw-leading-1 tw-m-0 tw-text-sm tw-font-normal tw-text-primary-grey',
        className
      )}
      {...props}
    >
      {children || (content && <div dangerouslySetInnerHTML={{ __html: content }} />)}
    </div>
  );
};

// Divider Component
interface SlackPreviewDividerProps extends React.ComponentPropsWithoutRef<typeof Separator> {}

const SlackPreviewDivider: React.FC<SlackPreviewDividerProps> = ({ className, ...props }) => {
  return <Separator className={cn('tw-my-4', className)} {...props} />;
};

// Event Details Component
interface SlackPreviewEventDetailsProps extends React.HTMLAttributes<HTMLDivElement> {
  event: EditableEvent | PublishedEvent;
}

const SlackPreviewEventDetails: React.FC<SlackPreviewEventDetailsProps> = ({
  event,
  className,
  ...props
}) => {
  const { title, hostOrganization, photoUrl } = event;

  const dateString = getEventDateString(event);

  return (
    <div className={cn('tw-flex tw-items-start tw-justify-between', className)} {...props}>
      <div className="tw-flex tw-flex-col">
        <p className="tw-leading-2 tw-m-0 tw-text-sm tw-font-semibold tw-text-primary-blue">
          {title}
        </p>
        {hostOrganization?.name && (
          <p className="tw-m-0 tw-text-sm tw-font-semibold tw-leading-none tw-text-primary-grey">
            {hostOrganization.name}
          </p>
        )}
        <p className="tw-m-0 tw-text-sm tw-font-normal tw-text-primary-grey">{dateString}</p>
      </div>
      {photoUrl && <CoverPhoto src={photoUrl} alt="Event Photo" className="tw-w-[88px]" />}
    </div>
  );
};

// Actions Component (wrapper for buttons)
interface SlackPreviewActionsProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

const SlackPreviewActions: React.FC<SlackPreviewActionsProps> = ({
  children,
  className,
  ...props
}) => {
  return (
    <div className={cn('tw-mt-3 tw-flex tw-gap-2', className)} {...props}>
      {children}
    </div>
  );
};

// Compound component exports
const SlackPreviewCompound = Object.assign(SlackPreview, {
  Message: SlackMessage,
  Divider: SlackPreviewDivider,
  EventDetails: SlackPreviewEventDetails,
  Actions: SlackPreviewActions
});

export { SlackPreviewCompound as SlackPreview };
